import { Card, CardContent, ChartContainer, ChartTooltip, ChartTooltipContent } from "@repo/ui";
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Sun,
} from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { useAirQuality, useWeather } from "@/lib/queries/use-weather";
import { useUserLocation } from "@/lib/use-location";
import { usePlaceName } from "@/lib/use-place-name";

function formatHourLabel(ts: string) {
  const d = new Date(ts);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function IconForCode({ code, className = "h-12 w-12" }: { code?: number; className?: string }) {
  // Mapping based on Open‑Meteo weather codes
  // 0 clear, 1-3 partly cloudy, 45-48 fog, 51-67 drizzle/rain, 71-77 snow, 80-82 showers, 95-99 thunder

  const c = code ?? 0;
  let Icon = Sun;
  if (c === 0) Icon = Sun;
  else if (c >= 1 && c <= 3) Icon = CloudSun;
  else if (c === 45 || c === 48) Icon = CloudFog;
  else if ((c >= 51 && c <= 57) || (c >= 61 && c <= 67)) Icon = CloudRain;
  else if (c >= 71 && c <= 77) Icon = CloudSnow;
  else if (c >= 80 && c <= 82) Icon = CloudDrizzle;
  else if (c >= 95 && c <= 99) Icon = CloudLightning;
  else Icon = Cloud;
  return <Icon className={className} />;
}

function CurrentIconBox() {
  const { lat, lon } = useUserLocation();
  const { data } = useWeather(lat, lon);
  const code = data?.current.weather_code;
  return (
    <div className="h-16 w-16 rounded-xl bg-secondary grid place-items-center text-primary">
      <IconForCode code={code} className="h-10 w-10" />
    </div>
  );
}

function HeaderLocation() {
  const { lat, lon } = useUserLocation();
  const place = usePlaceName(lat, lon);
  const coords = lat && lon ? `${lat.toFixed(3)}, ${lon.toFixed(3)}` : "--";
  return <div className="text-sm text-muted-foreground">{place ?? coords} • English • °C</div>;
}

function HeroSection() {
  const { lat, lon } = useUserLocation();
  const { data, isLoading } = useWeather(lat, lon);
  const current = data?.current;
  return (
    <section>
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-muted-foreground text-sm">Now near you</div>
                <div className="text-6xl font-semibold tracking-tight mt-2">
                  {isLoading ? "--" : Math.round(current?.temperature_2m ?? 0)}°C
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  Feels like {isLoading ? "--" : Math.round(current?.apparent_temperature ?? 0)}° •
                  Humidity {isLoading ? "--" : Math.round(current?.relative_humidity_2m ?? 0)}% •
                  Wind {isLoading ? "--" : Math.round(current?.wind_speed_10m ?? 0)} km/h
                </div>
              </div>
              <CurrentIconBox />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground">Precipitation</div>
            <div className="text-2xl font-semibold mt-1">
              {isLoading ? "--" : Math.round((current?.precipitation ?? 0) * 10) / 10} mm
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Updated{" "}
              {isLoading
                ? "--"
                : new Date(current!.time).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function ForecastAndAQI() {
  const { lat, lon } = useUserLocation();
  const { data } = useWeather(lat, lon);
  const aq = useAirQuality(lat, lon);

  const hourly = (data?.hourly.time || []).slice(0, 24).map((t, i) => ({
    time: t,
    temp: data?.hourly.temperature_2m[i] ?? 0,
    precip: data?.hourly.precipitation_probability[i] ?? 0,
  }));

  return (
    <section className="grid md:grid-cols-3 gap-4">
      <Card className="md:col-span-2">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Next 24 hours</h2>
            <div className="text-sm text-muted-foreground">Hourly temperature</div>
          </div>
          <ChartContainer
            config={{ temp: { label: "Temp", color: "hsl(var(--chart-1))" } }}
            className="h-56"
          >
            <AreaChart data={hourly} margin={{ left: 6, right: 6, top: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="temp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="time" tickFormatter={(v) => formatHourLabel(v)} minTickGap={20} />
              <YAxis width={30} tickFormatter={(v) => `${Math.round(v)}°`} />
              <Area
                type="monotone"
                dataKey="temp"
                stroke="hsl(var(--chart-1))"
                fill="url(#temp)"
                strokeWidth={2}
              />
              <ChartTooltip content={<ChartTooltipContent nameKey="temp" />} />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-2">Air quality</h2>
          <AQISummary
            aqi={aq.data?.hourly.european_aqi?.[0]}
            pm25={aq.data?.hourly.pm2_5?.[0]}
            pm10={aq.data?.hourly.pm10?.[0]}
          />
        </CardContent>
      </Card>

      <Card className="md:col-span-3">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-4">7‑day outlook</h2>
          <SevenDay />
        </CardContent>
      </Card>
    </section>
  );
}

function aqiText(aqi?: number) {
  if (aqi == null) return { label: "--", color: "text-muted-foreground" } as const;
  if (aqi <= 20) return { label: "Good", color: "text-emerald-600" } as const;
  if (aqi <= 40) return { label: "Fair", color: "text-lime-600" } as const;
  if (aqi <= 60) return { label: "Moderate", color: "text-yellow-600" } as const;
  if (aqi <= 80) return { label: "Poor", color: "text-orange-600" } as const;
  if (aqi <= 100) return { label: "Very Poor", color: "text-red-600" } as const;
  return { label: "Extremely Poor", color: "text-red-700" } as const;
}

function AQISummary({ aqi, pm25, pm10 }: { aqi?: number; pm25?: number; pm10?: number }) {
  const t = aqiText(aqi);
  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">European AQI</div>
      <div className="text-3xl font-semibold">
        {aqi ?? "--"} <span className={`text-base font-medium ${t.color}`}>{t.label}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-lg bg-secondary p-3">
          <div className="text-xs text-muted-foreground">PM2.5</div>
          <div className="text-lg font-semibold">{pm25?.toFixed(1) ?? "--"} µg/m³</div>
        </div>
        <div className="rounded-lg bg-secondary p-3">
          <div className="text-xs text-muted-foreground">PM10</div>
          <div className="text-lg font-semibold">{pm10?.toFixed(1) ?? "--"} µg/m³</div>
        </div>
      </div>
    </div>
  );
}

function SevenDay() {
  const { lat, lon } = useUserLocation();
  const { data } = useWeather(lat, lon);
  const days = (data?.daily.time || []).map((t, i) => ({
    date: t,
    tMax: data?.daily.temperature_2m_max[i] ?? 0,
    tMin: data?.daily.temperature_2m_min[i] ?? 0,
    rain: data?.daily.precipitation_sum[i] ?? 0,
    code: data?.daily.weather_code[i] ?? 0,
  }));
  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
      {days.map((d) => (
        <div key={d.date} className="rounded-xl border p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {new Date(d.date).toLocaleDateString("en-GB", { weekday: "short" })}
            </div>
            <IconForCode code={d.code} className="h-5 w-5 text-primary" />
          </div>
          <div className="text-lg font-semibold mt-1">
            {Math.round(d.tMax)}° / {Math.round(d.tMin)}°
          </div>
          <div className="text-xs text-muted-foreground mt-1">Rain {Math.round(d.rain)} mm</div>
        </div>
      ))}
    </div>
  );
}

function RadarSection() {
  const { lat, lon } = useUserLocation();
  const windySrc = `https://embed.windy.com/embed2.html?lat=${lat ?? 13.7563}&lon=${lon ?? 100.5018}&detailLat=${lat ?? 13.7563}&detailLon=${lon ?? 100.5018}&zoom=10&level=surface&overlay=radar&menu=&message=true&type=map&location=coordinates&metricWind=kmh&metricTemp=%C2%B0C&radarRange=-1`;
  return (
    <section>
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-6 pt-6">
            <h2 className="text-lg font-semibold">Rain radar</h2>
            <span className="text-sm text-muted-foreground">Live • Bangkok</span>
          </div>
          <div className="mt-4 aspect-[16/10] w-full overflow-hidden rounded-b-xl relative">
            <iframe
              title="Live Rain Radar"
              width="100%"
              height="100%"
              src={windySrc}
              frameBorder={0}
            />
            <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <span className="block h-3 w-3 rounded-full bg-primary shadow ring-2 ring-white" />
              <span className="block h-3 w-3 rounded-full bg-primary/40 animate-ping" />
            </span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Footer() {
  return (
    <footer className="pt-4 text-center text-xs text-muted-foreground">
      Data: Open‑Meteo (weather & air quality) • Radar: Windy.com. Local time:{" "}
      {new Date().toLocaleTimeString("en-GB", { timeStyle: "short" })}
    </footer>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="px-6 md:px-10 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <div className="h-8 w-8 rounded-md bg-primary/90" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">JennSky</h1>
          </div>
          <HeaderLocation />
        </div>
      </header>

      <main className="px-6 md:px-10 pb-10 space-y-8 max-w-6xl mx-auto">
        <HeroSection />
        <ForecastAndAQI />
        <RadarSection />
        <Footer />
      </main>
    </div>
  );
}
